import { isEqual } from "es-toolkit";
import { isEmpty } from "es-toolkit/compat";
import { type Getter, type Setter, atom } from "jotai";
import { atomFamily } from "jotai/utils";
import type { Mark } from "prosemirror-model";

import type { PageMetadata } from "@moment/api-collab/api-types";
import { schema } from "@moment/prosemirror-schema";

import { $loggedInUser } from "../authenication";
import {
	$document,
	docsListCollabEntry,
	docsListItemLocalEntry,
	docsListItemRemoteEntry,
} from "../document-list";
import { env } from "../env";
import { $gitStatus } from "../git";
import { buildDefaultPagePath } from "../page-metadata/page-metadata.atom";
import { $editorState, $flushEditorStateSideEffects } from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import { $loadEditorStateFromDisk } from "../prosemirror/load-editor-state-from-disk.atom";
import { $loadEditorStateFromReboot } from "../prosemirror/load-editor-state-from-reboot.atom";
import { $loadEditorStateFromRemote } from "../prosemirror/load-editor-state-from-remote.atom";
import { $pluginFactory } from "../prosemirror/plugins-factory.atom";
import {
	buildPageLinkText,
	computeLinkTextUpdates,
	resolvePageFromLinkText,
	resolvePageReference,
} from "./page-link.utils";

async function ensureEditorStateLoaded(
	get: Getter,
	set: Setter,
	params: {
		documentId: string;
		page: PageMetadata;
		sourcePage?: PageMetadata;
		pages: PageMetadata[];
		editorStateKey: string;
	}
) {
	const { documentId, page, sourcePage, editorStateKey } = params;
	const { pages } = params;
	const existingEditorState = get($editorState(editorStateKey));
	if (existingEditorState) {
		return existingEditorState;
	}

	const pluginFactory = get($pluginFactory);
	if (!pluginFactory) {
		throw new Error("ProseMirror plugin factory is not registered");
	}

	const document = get($document(documentId));
	if (!document) {
		throw new Error(`Document ${documentId} not found while updating page links`);
	}

	const gitStatus = await get($gitStatus(documentId));
	const remoteEntry = docsListItemRemoteEntry(document);
	const collabEntry = docsListCollabEntry(remoteEntry);
	const localEntry = docsListItemLocalEntry(document);
	const hasLocalEntry = !isEmpty(document.local);
	const hasRemoteEntry = !isEmpty(document.remote);
	const hasCollab = !isEmpty(remoteEntry?.collab);
	const isOnCollabBranch = gitStatus?.branch === "collab/main";
	const isConflicted = gitStatus?.isConflicted ?? false;
	const shouldUseCollab = hasCollab && (!hasLocalEntry || isOnCollabBranch) && !isConflicted;
	const readonly = !(env.isDesktopApp || env.isMobileApp);
	const pagePath =
		page.path && page.path !== sourcePage?.path ? page.path : buildDefaultPagePath(pages, page);

	if (hasLocalEntry && !shouldUseCollab) {
		await set($loadEditorStateFromDisk, {
			key: editorStateKey,
			document,
			pageId: page.id,
			pagePath,
			plugins: pluginFactory({
				documentId,
				pageId: page.id,
				schema,
				readonly,
				version: undefined,
			}),
		});
	} else if (shouldUseCollab && remoteEntry?.workspace.slug && collabEntry && gitStatus) {
		const loggedInUser = get($loggedInUser);
		if (!loggedInUser?.sub) {
			throw new Error("Cannot update collab page links without an authenticated user");
		}

		await set($loadEditorStateFromReboot, {
			key: editorStateKey,
			workspaceSlug: remoteEntry.workspace.slug,
			documentId,
			pageId: page.id,
			branch: collabEntry.branch ?? gitStatus.branch,
			sha: collabEntry.sha ?? gitStatus.commit,
			pluginFactory,
			schema,
			readonly,
			localEntry,
			userId: loggedInUser.sub,
			pagePath,
		});
	} else if (hasRemoteEntry) {
		await set($loadEditorStateFromRemote, {
			key: editorStateKey,
			document,
			pageId: page.id,
			pagePath,
			ref: gitStatus?.branch ?? "main",
			plugins: pluginFactory({
				documentId,
				pageId: page.id,
				schema,
				readonly,
				version: undefined,
			}),
			ssr: true,
		});
	}

	const loadedEditorState = get($editorState(editorStateKey));
	if (!loadedEditorState) {
		throw new Error(`Editor state for page ${page.id} is still unavailable after loading`);
	}

	return loadedEditorState;
}

function getUpdatedPageRef(
	pageRef: string,
	linkTextUpdates: Map<string, string>,
	pagesBefore: PageMetadata[],
	currentPageId: string
): string | null {
	const directUpdate = linkTextUpdates.get(pageRef);
	if (directUpdate && directUpdate !== pageRef) {
		return directUpdate;
	}

	const resolvedReference = resolvePageReference(pageRef, pagesBefore, currentPageId);
	if (!resolvedReference?.fragment) {
		return null;
	}

	const basePageRef = buildPageLinkText(resolvedReference.page.id, pagesBefore);
	const updatedBaseRef = linkTextUpdates.get(basePageRef);
	if (!updatedBaseRef || updatedBaseRef === basePageRef) {
		return null;
	}

	return `${updatedBaseRef}#${resolvedReference.fragment}`;
}

function getPreferredPageRef(pageId: string, pages: PageMetadata[], currentPageId: string): string {
	const canonicalPageRef = buildPageLinkText(pageId, pages);
	const page = pages.find((candidate) => candidate.id === pageId);
	if (!page) {
		return canonicalPageRef;
	}

	const bareTitlePageRef = buildPageLinkText(pageId, [{ ...page, parentPageID: null }]);
	if (resolvePageFromLinkText(bareTitlePageRef, pages, currentPageId)?.id === pageId) {
		return bareTitlePageRef;
	}

	return canonicalPageRef;
}

function getUpdatedResolvedPageRef(
	pageRef: string,
	pagesBefore: PageMetadata[],
	pagesAfter: PageMetadata[],
	currentPageId: string
): string | null {
	const resolvedReference = resolvePageReference(pageRef, pagesBefore, currentPageId);
	if (!resolvedReference) {
		return null;
	}

	const pageAfter = pagesAfter.find((page) => page.id === resolvedReference.page.id);
	if (!pageAfter) {
		return null;
	}

	const updatedBasePageRef = getPreferredPageRef(pageAfter.id, pagesAfter, currentPageId);
	const updatedPageRef = resolvedReference.fragment
		? `${updatedBasePageRef}#${resolvedReference.fragment}`
		: updatedBasePageRef;

	return updatedPageRef !== pageRef ? updatedPageRef : null;
}

/**
 * Atom that updates all page links in a document when pages change.
 *
 * Call this BEFORE updating metadata, passing the before and after states.
 * It handles all cases: rename, move, parent rename, orphaning, etc.
 *
 * Updates both:
 * - page_link nodes (inline [[Page Title]] links)
 * - link marks with page: protocol ([text](page:Title) links)
 *
 * This traverses all pages' ProseMirror documents BACKWARDS (by node position)
 * to update links, ensuring position offsets remain valid during edits.
 *
 * Uses $editorState (the main editor state atom) so that changes are automatically
 * synced to any mounted ProseMirror views via the controlled component pattern.
 */
export const $updatePageLinks = atomFamily(
	({ documentId }: { documentId: string }) =>
		atom(
			null,
			async (
				get,
				set,
				params: {
					pagesBefore: PageMetadata[];
					pagesAfter: PageMetadata[];
				}
			) => {
				const { pagesBefore, pagesAfter } = params;

				// Compute what link text changes are needed
				const linkTextUpdates = computeLinkTextUpdates(pagesBefore, pagesAfter);

				// If no updates needed, return early
				if (linkTextUpdates.size === 0) {
					return;
				}

				// Iterate through all pages and update their page links
				for (const page of pagesAfter) {
					// Only markdown pages have ProseMirror editor state. Canvas pages
					// use a separate tldraw snapshot loader, and request pages are stored
					// as index.yml. Routing either through the markdown loader here can
					// create invalid files or throw during lazy-load rewrites.
					if (page.type !== undefined && page.type !== "markdown") {
						continue;
					}

					const editorStateKey = editorStateKeyForDocsRoute({
						documentId,
						pageId: page.id,
					});
					const editorState = await ensureEditorStateLoaded(get, set, {
						documentId,
						page,
						sourcePage: pagesBefore.find((candidate) => candidate.id === page.id),
						pages: pagesAfter,
						editorStateKey,
					});

					// Collect all page_link nodes with their positions
					const pageLinks: {
						pos: number;
						pageTitle: string;
						attrs: Record<string, unknown>;
					}[] = [];

					// Collect all link marks with page: protocol
					const pageLinkMarks: {
						from: number;
						to: number;
						mark: Mark;
						pageRef: string;
					}[] = [];

					editorState.doc.descendants((node, pos) => {
						// Check for page_link nodes
						if (node.type === schema.nodes.page_link) {
							const pageTitle = node.attrs["pageTitle"] as string;
							pageLinks.push({ pos, pageTitle, attrs: node.attrs });
						}

						// Check for link marks with page: protocol
						if (node.isText) {
							const linkMark = schema.marks.link.isInSet(node.marks);
							if (linkMark) {
								const href = linkMark.attrs["href"] as string;
								if (href.startsWith("page:")) {
									const pageRef = href.slice(5); // Remove "page:" prefix
									pageLinkMarks.push({
										from: pos,
										to: pos + node.nodeSize,
										mark: linkMark,
										pageRef,
									});
								}
							}
						}
					});

					// CRITICAL: Sort by position DESCENDING (backward traversal)
					// This ensures that when we modify a node/mark, earlier positions stay valid
					pageLinks.sort((a, b) => b.pos - a.pos);
					pageLinkMarks.sort((a, b) => b.from - a.from);

					// Build the transaction
					let tr = editorState.tr;
					let hasChanges = false;

					// Update page_link nodes
					for (const { pos, pageTitle, attrs } of pageLinks) {
						const updatedTitle =
							getUpdatedPageRef(pageTitle, linkTextUpdates, pagesBefore, page.id) ??
							getUpdatedResolvedPageRef(pageTitle, pagesBefore, pagesAfter, page.id);
						if (updatedTitle && updatedTitle !== pageTitle) {
							tr = tr.setNodeMarkup(pos, null, {
								...attrs,
								pageTitle: updatedTitle,
							});
							hasChanges = true;
						}
					}

					// Update link marks with page: protocol
					for (const { from, to, mark, pageRef } of pageLinkMarks) {
						const updatedPageRef =
							getUpdatedPageRef(pageRef, linkTextUpdates, pagesBefore, page.id) ??
							getUpdatedResolvedPageRef(pageRef, pagesBefore, pagesAfter, page.id);
						if (updatedPageRef && updatedPageRef !== pageRef) {
							// Remove old mark and add new one with updated href
							const newMark = schema.marks.link.create({
								...mark.attrs,
								href: `page:${updatedPageRef}`,
							});
							tr = tr.removeMark(from, to, mark).addMark(from, to, newMark);
							hasChanges = true;
						}
					}

					// Apply changes if any were made
					// The transaction is passed to $editorState which will:
					// 1. Apply it to the atom state
					// 2. Trigger re-render in any mounted ProseMirror views
					// 3. Trigger persistence via $editorStateEffect
					if (hasChanges) {
						set($editorState(editorStateKey), tr);
						set($flushEditorStateSideEffects, editorStateKey);
					}
				}
			}
		),
	isEqual
);
